import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthRoles } from '@app/api/dmc';
import { CurrentUser, UserService } from '@app/core/services/user.service';

/**
 * Route guard function that checks if a user has the required roles to access a route.
  */
export function hasRoleGuard(requiredRoles: AuthRoles[]): CanActivateFn {
  return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
    const userService = inject(UserService);
    const router = inject(Router);

    const routeRoles = route.data['requiredRoles'] as AuthRoles[] | undefined;
    const rolesToCheck = routeRoles || requiredRoles;

    if (!rolesToCheck || rolesToCheck.length === 0) {
      return true;
    }

    const user = await userService.fetchUser();
    if (!user) {
      userService.login(state.url);
      return false;
    }

    return checkUserRoles(user, rolesToCheck, router);
  };
}

/**
 * Function to check if a user has the required roles to enable buttons
 */
export function hasRole(requiredRoles: AuthRoles[]): boolean {
  const userService = inject(UserService);
  void userService.fetchUser();
  const user = userService.user();
  const roles = user?.roles ?? [];

  return requiredRoles.some(r => roles.includes(r));
}

/**
 * Helper function to check if a user has the required roles
 */
function checkUserRoles(user: CurrentUser, requiredRoles: AuthRoles[], router: Router): boolean {
  const hasRequiredRole = requiredRoles.some(role => user.roles.includes(role));

  if (!hasRequiredRole) {
    void router.navigate(['/error/401']);
    return false;
  }

  return true;
}
